<script setup lang="ts">
import { nextTick, onMounted, ref, useTemplateRef, watch } from "vue";

import { useNativeEvents } from "@/composables/useNativeEvents";

import TreeNode from "./TreeNode.vue";
import type { TreeNodeData, TreeNodeInstance } from "./types";

const { addEventListener } = useNativeEvents();

const props = withDefaults(
  defineProps<{
    nodes: TreeNodeData[];
    showCheckboxes?: boolean;
    expandedByDefault?: boolean;
  }>(),
  {
    showCheckboxes: false,
    expandedByDefault: false,
  },
);

const emit = defineEmits<{
  select: [node: TreeNodeData];
  "toggle-checkbox": [node: TreeNodeData, checked: boolean];
}>();

const treeRef = useTemplateRef("tree");
const nodeRefs = ref<Record<string, TreeNodeInstance | null>>({});

const setNodeRef = (key: TreeNodeData["id"], cmp: TreeNodeInstance | null) => {
  nodeRefs.value[key] = cmp;
};

const selectedNodeId = ref<string | null>(null);

const handleSelectNode = (node: TreeNodeData) => {
  selectedNodeId.value = node.id;
  emit("select", node);
};

const handleToggleCheckbox = (node: TreeNodeData, checked: boolean) => {
  emit("toggle-checkbox", node, checked);
};

function findSelectedNode(nodes: TreeNodeData[]): TreeNodeData | null {
  for (const node of nodes) {
    if (node.selected) return node;
    const foundChild = findSelectedNode(node.children);
    if (foundChild) return foundChild;
  }
  return null;
}

async function expandParentNodes(
  nodes: TreeNodeData[],
  nodeId: string | null,
  parents: TreeNodeData[] = [],
): Promise<void> {
  if (!nodeId) return;
  for (const node of nodes) {
    if (node.id === nodeId) {
      for (const parent of parents) {
        const parentRef = nodeRefs.value[parent.id];
        if (parentRef) {
          parentRef.expand();
          await nextTick();
        }
      }
      return;
    }

    if (node.children.length > 0) {
      await expandParentNodes(node.children, nodeId, [...parents, node]);
    }
  }
}

onMounted(() => {
  const initialSelectedNode = findSelectedNode(props.nodes);
  if (initialSelectedNode) {
    handleSelectNode(initialSelectedNode);
    void expandParentNodes(props.nodes, selectedNodeId.value);
  }

  if (treeRef.value) {
    addEventListener(treeRef.value, "focusout", handleFocusOut);
  }
});

watch(
  () => props.nodes,
  () => {
    const initialSelectedNode = findSelectedNode(props.nodes);
    selectedNodeId.value = initialSelectedNode?.id ?? null;
    if (initialSelectedNode) {
      void expandParentNodes(props.nodes, selectedNodeId.value);
    }
  },
  { deep: true },
);

const handleFocusOut = (event: FocusEvent) => {
  if (!treeRef.value?.contains(event.relatedTarget as Node) && selectedNodeId.value) {
    void expandParentNodes(props.nodes, selectedNodeId.value);
  }
};
</script>

<template>
  <div ref="tree" role="tree" aria-label="Tree View">
    <TreeNode
      v-for="(node, index) in nodes"
      :ref="(ref) => setNodeRef(node.id, ref as TreeNodeInstance)"
      :key="node.id"
      :node
      :selected-node-id
      :indentation="8"
      :is-first-node="index === 0"
      :set-node-ref
      :show-checkboxes
      :expanded-by-default
      @select-node="handleSelectNode"
      @toggle-checkbox="handleToggleCheckbox"
    />
  </div>
</template>
